/**
 * userStore — the single Zustand store for the learner (user) experience.
 *
 * Combines every learner slice (auth, profile, dashboard, catalog, learning &
 * purchases, guest public catalog, uploads) and exposes dedicated selector
 * hooks.
 *
 *   import { useUserDashboard } from "../store/user";
 */
import { create } from "zustand";
import { useShallow } from "zustand/react/shallow";
import authSession from "../../lib/authSession";

import { userAuthInitialState, createUserAuthActions } from "./userAuthStore";
import { userProfileInitialState, createUserProfileActions } from "./userProfileStore";
import { userDashboardInitialState, createUserDashboardActions } from "./userDashboardStore";
import { userCatalogInitialState, createUserCatalogActions } from "./userCatalogStore";
import { userLearningInitialState, createUserLearningActions } from "./userLearningStore";
import {
  userLearningPathInitialState,
  createUserLearningPathActions,
} from "./userLearningPathStore";
import {
  userNotificationInitialState,
  createUserNotificationActions,
} from "./userNotificationStore";
import {
  userPublicCatalogInitialState,
  createUserPublicCatalogActions,
} from "./userPublicCatalogStore";
import { userUploadInitialState, createUserUploadActions } from "./userUploadStore";

export const useUserStore = create((set, get) => ({
  // Slice state
  ...userAuthInitialState,
  ...userProfileInitialState,
  ...userDashboardInitialState,
  ...userCatalogInitialState,
  ...userLearningInitialState,
  ...userLearningPathInitialState,
  ...userNotificationInitialState,
  ...userPublicCatalogInitialState,
  ...userUploadInitialState,

  // Shared message state
  successMessage: null,
  errorMessage: null,

  // Slice actions
  ...createUserAuthActions(set, get),
  ...createUserProfileActions(set, get),
  ...createUserDashboardActions(set, get),
  ...createUserCatalogActions(set, get),
  ...createUserLearningActions(set, get),
  ...createUserLearningPathActions(set, get),
  ...createUserNotificationActions(set, get),
  ...createUserPublicCatalogActions(set, get),
  ...createUserUploadActions(set, get),

  // Shared helpers
  getToken: () => get().userToken || authSession.getToken(),
  clearMessage: () => set({ successMessage: null, errorMessage: null }),
}));

// ── Selector hooks ───────────────────────────────────────────────────────────

/** Auth selectors. */
export const useUserAuth = () =>
  useUserStore(
    useShallow((s) => ({
      userToken: s.userToken,
      user: s.user,
      userIsAuthenticated: s.userIsAuthenticated,
      userLoginLoading: s.userLoginLoading,
      userLoginError: s.userLoginError,
      userAuthActionLoading: s.userAuthActionLoading,
      userAuthActionError: s.userAuthActionError,
      userLogin: s.userLogin,
      userGoogleLogin: s.userGoogleLogin,
      userLogout: s.userLogout,
      userRefresh: s.userRefresh,
      userRegister: s.userRegister,
      userChangePassword: s.userChangePassword,
      userForgotPassword: s.userForgotPassword,
      userResetPassword: s.userResetPassword,
      userResendVerification: s.userResendVerification,
      userVerifyEmail: s.userVerifyEmail,
    }))
  );

/** Profile selectors (GET/PATCH /api/learners/me). */
export const useUserProfile = () =>
  useUserStore(
    useShallow((s) => ({
      userProfile: s.userProfile,
      userProfileLoading: s.userProfileLoading,
      userProfileError: s.userProfileError,
      fetchUserProfile: s.fetchUserProfile,
      updateUserProfile: s.updateUserProfile,
    }))
  );

/** Dashboard selectors (/api/learners/dashboard*). */
export const useUserDashboard = () =>
  useUserStore(
    useShallow((s) => ({
      userDashboard: s.userDashboard,
      userDashboardView: s.userDashboardView,
      userDashboardLoading: s.userDashboardLoading,
      userDashboardError: s.userDashboardError,
      fetchUserDashboard: s.fetchUserDashboard,

      continueLearning: s.continueLearning,
      continueLearningLoading: s.continueLearningLoading,
      continueLearningError: s.continueLearningError,
      fetchContinueLearning: s.fetchContinueLearning,

      dashboardNews: s.dashboardNews,
      dashboardNewsLoading: s.dashboardNewsLoading,
      dashboardNewsError: s.dashboardNewsError,
      fetchDashboardNews: s.fetchDashboardNews,

      recentResources: s.recentResources,
      recentResourcesLoading: s.recentResourcesLoading,
      recentResourcesError: s.recentResourcesError,
      fetchRecentResources: s.fetchRecentResources,

      recentTransactions: s.recentTransactions,
      recentTransactionsLoading: s.recentTransactionsLoading,
      recentTransactionsError: s.recentTransactionsError,
      fetchRecentTransactions: s.fetchRecentTransactions,
    }))
  );

/** Catalog selectors (courses / books / journals / categories / search). */
export const useUserCatalog = () =>
  useUserStore(
    useShallow((s) => ({
      userCategories: s.userCategories,
      userCategoriesLoading: s.userCategoriesLoading,
      userCategoriesError: s.userCategoriesError,
      fetchUserCategories: s.fetchUserCategories,

      userCourses: s.userCourses,
      userCoursesLoading: s.userCoursesLoading,
      userCoursesError: s.userCoursesError,
      userCoursesTotal: s.userCoursesTotal,
      userCoursesPages: s.userCoursesPages,
      fetchUserCourses: s.fetchUserCourses,

      userCourseDetail: s.userCourseDetail,
      userCourseDetailLoading: s.userCourseDetailLoading,
      userCourseDetailError: s.userCourseDetailError,
      fetchUserCourse: s.fetchUserCourse,

      userRelatedCourses: s.userRelatedCourses,
      userRelatedCoursesLoading: s.userRelatedCoursesLoading,
      userRelatedCoursesError: s.userRelatedCoursesError,
      userRelatedCoursesTotal: s.userRelatedCoursesTotal,
      userRelatedCoursesPages: s.userRelatedCoursesPages,
      fetchUserRelatedCourses: s.fetchUserRelatedCourses,

      userBooks: s.userBooks,
      userBooksLoading: s.userBooksLoading,
      userBooksError: s.userBooksError,
      userBooksTotal: s.userBooksTotal,
      userBooksPages: s.userBooksPages,
      fetchUserBooks: s.fetchUserBooks,

      userBookDetail: s.userBookDetail,
      userBookDetailLoading: s.userBookDetailLoading,
      userBookDetailError: s.userBookDetailError,
      fetchUserBook: s.fetchUserBook,

      userJournals: s.userJournals,
      userJournalsLoading: s.userJournalsLoading,
      userJournalsError: s.userJournalsError,
      userJournalsTotal: s.userJournalsTotal,
      userJournalsPages: s.userJournalsPages,
      fetchUserJournals: s.fetchUserJournals,

      userJournalDetail: s.userJournalDetail,
      userJournalDetailLoading: s.userJournalDetailLoading,
      userJournalDetailError: s.userJournalDetailError,
      fetchUserJournal: s.fetchUserJournal,

      userSearchResults: s.userSearchResults,
      userSearchLoading: s.userSearchLoading,
      userSearchError: s.userSearchError,
      fetchUserSearch: s.fetchUserSearch,
    }))
  );

/** Learning & purchases selectors. */
export const useUserLearning = () =>
  useUserStore(
    useShallow((s) => ({
      enrolledCourses: s.enrolledCourses,
      enrolledCoursesLoading: s.enrolledCoursesLoading,
      enrolledCoursesError: s.enrolledCoursesError,
      fetchEnrolledCourses: s.fetchEnrolledCourses,

      learnView: s.learnView,
      learnViewLoading: s.learnViewLoading,
      learnViewError: s.learnViewError,
      fetchLearnView: s.fetchLearnView,

      userLibrary: s.userLibrary,
      userLibraryLoading: s.userLibraryLoading,
      userLibraryError: s.userLibraryError,
      userLibraryTotal: s.userLibraryTotal,
      userLibraryPages: s.userLibraryPages,
      fetchUserLibrary: s.fetchUserLibrary,
      myLibraryResources: s.myLibraryResources,
      myLibraryCourses: s.myLibraryCourses,
      myLibraryLoading: s.myLibraryLoading,
      myLibraryError: s.myLibraryError,
      fetchMyLibrary: s.fetchMyLibrary,
      downloadMyLibraryResource: s.downloadMyLibraryResource,

      userPurchases: s.userPurchases,
      userPurchasesLoading: s.userPurchasesLoading,
      userPurchasesError: s.userPurchasesError,
      userPurchasesTotal: s.userPurchasesTotal,
      userPurchasesPages: s.userPurchasesPages,
      fetchUserPurchases: s.fetchUserPurchases,

      userPurchaseDetail: s.userPurchaseDetail,
      userPurchaseDetailLoading: s.userPurchaseDetailLoading,
      userPurchaseDetailError: s.userPurchaseDetailError,
      fetchUserPurchase: s.fetchUserPurchase,
      verifyUserPurchase: s.verifyUserPurchase,

      userPurchaseMutationLoading: s.userPurchaseMutationLoading,
      userPurchaseMutationError: s.userPurchaseMutationError,
      purchaseUserCourse: s.purchaseUserCourse,
      purchaseUserBook: s.purchaseUserBook,
      purchaseUserJournal: s.purchaseUserJournal,

      paymentInitResult: s.paymentInitResult,
      paymentInitLoading: s.paymentInitLoading,
      paymentInitError: s.paymentInitError,
      initializeUserPayment: s.initializeUserPayment,
      addUserCartItem: s.addUserCartItem,
      checkoutUserCart: s.checkoutUserCart,

      paymentVerifyResult: s.paymentVerifyResult,
      paymentVerifyLoading: s.paymentVerifyLoading,
      paymentVerifyError: s.paymentVerifyError,
      verifyUserPayment: s.verifyUserPayment,

      continueCourse: s.continueCourse,
      continueCourseLoading: s.continueCourseLoading,
      continueCourseError: s.continueCourseError,
      fetchContinueCourse: s.fetchContinueCourse,

      courseLesson: s.courseLesson,
      courseLessonLoading: s.courseLessonLoading,
      courseLessonError: s.courseLessonError,
      fetchCourseLesson: s.fetchCourseLesson,
      quizAttempts: s.quizAttempts,
      quizAttemptsLoading: s.quizAttemptsLoading,
      quizAttemptsError: s.quizAttemptsError,
      quizAttemptsTotal: s.quizAttemptsTotal,
      quizAttemptsPages: s.quizAttemptsPages,
      fetchUserQuizAttempts: s.fetchUserQuizAttempts,
      quizAttemptResult: s.quizAttemptResult,
      quizAttemptSubmitLoading: s.quizAttemptSubmitLoading,
      quizAttemptSubmitError: s.quizAttemptSubmitError,
      submitUserQuizAttempt: s.submitUserQuizAttempt,
      lessonSubmission: s.lessonSubmission,
      lessonSubmissionLoading: s.lessonSubmissionLoading,
      lessonSubmissionError: s.lessonSubmissionError,
      fetchUserLessonSubmission: s.fetchUserLessonSubmission,
      lessonSubmissionSubmitLoading: s.lessonSubmissionSubmitLoading,
      lessonSubmissionSubmitError: s.lessonSubmissionSubmitError,
      submitUserLessonSubmission: s.submitUserLessonSubmission,
      lessonCompleteLoading: s.lessonCompleteLoading,
      lessonCompleteError: s.lessonCompleteError,
      completeLesson: s.completeLesson,

      libraryItem: s.libraryItem,
      libraryItemLoading: s.libraryItemLoading,
      libraryItemError: s.libraryItemError,
      libraryDownloadLoading: s.libraryDownloadLoading,
      libraryDownloadError: s.libraryDownloadError,
      fetchLibraryBook: s.fetchLibraryBook,
      fetchLibraryJournal: s.fetchLibraryJournal,
      downloadLibraryBook: s.downloadLibraryBook,
      downloadLibraryJournal: s.downloadLibraryJournal,
    }))
  );

/** Learner learning-path selectors (/api/learners/learning-paths*). */
export const useUserLearningPaths = () =>
  useUserStore(
    useShallow((s) => ({
      userLearningPaths: s.userLearningPaths,
      userLearningPathsLoading: s.userLearningPathsLoading,
      userLearningPathsError: s.userLearningPathsError,
      userLearningPathsTotal: s.userLearningPathsTotal,
      userLearningPathsPages: s.userLearningPathsPages,
      fetchUserLearningPaths: s.fetchUserLearningPaths,

      userLearningPath: s.userLearningPath,
      userLearningPathLoading: s.userLearningPathLoading,
      userLearningPathError: s.userLearningPathError,
      fetchUserLearningPath: s.fetchUserLearningPath,

      userLearningPathProgress: s.userLearningPathProgress,
      userLearningPathProgressLoading: s.userLearningPathProgressLoading,
      userLearningPathProgressError: s.userLearningPathProgressError,
      fetchUserLearningPathProgress: s.fetchUserLearningPathProgress,

      userLearningPathEnrollment: s.userLearningPathEnrollment,
      userLearningPathEnrollmentLoading: s.userLearningPathEnrollmentLoading,
      userLearningPathEnrollmentError: s.userLearningPathEnrollmentError,
      enrollUserLearningPath: s.enrollUserLearningPath,
    }))
  );

/** In-app notifications selectors (/api/learners/notifications*). */
export const useUserNotifications = () =>
  useUserStore(
    useShallow((s) => ({
      notifications: s.notifications,
      notificationsLoading: s.notificationsLoading,
      notificationsError: s.notificationsError,
      notificationsTotal: s.notificationsTotal,
      notificationsPages: s.notificationsPages,
      fetchNotifications: s.fetchNotifications,

      notificationDetail: s.notificationDetail,
      notificationDetailLoading: s.notificationDetailLoading,
      notificationDetailError: s.notificationDetailError,
      fetchNotification: s.fetchNotification,

      unreadCount: s.unreadCount,
      unreadCountLoading: s.unreadCountLoading,
      unreadCountError: s.unreadCountError,
      fetchUnreadCount: s.fetchUnreadCount,

      notificationMutationLoading: s.notificationMutationLoading,
      notificationMutationError: s.notificationMutationError,
      markNotificationRead: s.markNotificationRead,
      markAllNotificationsRead: s.markAllNotificationsRead,
    }))
  );

/** Guest catalog selectors (GET /api/public/catalog/*). */
export const useUserPublicCatalog = () =>
  useUserStore(
    useShallow((s) => ({
      publicTopics: s.publicTopics,
      publicTopicsLoading: s.publicTopicsLoading,
      publicTopicsError: s.publicTopicsError,
      fetchPublicTopics: s.fetchPublicTopics,
      publicDepartments: s.publicDepartments,
      publicDepartmentsLoading: s.publicDepartmentsLoading,
      publicDepartmentsError: s.publicDepartmentsError,
      fetchPublicDepartments: s.fetchPublicDepartments,
      publicCategories: s.publicCategories,
      publicCategoriesLoading: s.publicCategoriesLoading,
      publicCategoriesError: s.publicCategoriesError,
      fetchPublicCategories: s.fetchPublicCategories,

      publicBooks: s.publicBooks,
      publicBooksLoading: s.publicBooksLoading,
      publicBooksError: s.publicBooksError,
      publicBooksTotal: s.publicBooksTotal,
      publicBooksPages: s.publicBooksPages,
      fetchPublicBooks: s.fetchPublicBooks,

      publicBook: s.publicBook,
      publicBookLoading: s.publicBookLoading,
      publicBookError: s.publicBookError,
      fetchPublicBook: s.fetchPublicBook,

      publicCourses: s.publicCourses,
      publicCoursesLoading: s.publicCoursesLoading,
      publicCoursesError: s.publicCoursesError,
      publicCoursesTotal: s.publicCoursesTotal,
      publicCoursesPages: s.publicCoursesPages,
      fetchPublicCourses: s.fetchPublicCourses,

      publicTrendingCourses: s.publicTrendingCourses,
      publicTrendingCoursesLoading: s.publicTrendingCoursesLoading,
      publicTrendingCoursesError: s.publicTrendingCoursesError,
      fetchPublicTrendingCourses: s.fetchPublicTrendingCourses,

      publicCourse: s.publicCourse,
      publicCourseLoading: s.publicCourseLoading,
      publicCourseError: s.publicCourseError,
      fetchPublicCourse: s.fetchPublicCourse,

      publicCourseReviews: s.publicCourseReviews,
      publicCourseReviewsLoading: s.publicCourseReviewsLoading,
      publicCourseReviewsError: s.publicCourseReviewsError,
      publicCourseReviewsTotal: s.publicCourseReviewsTotal,
      publicCourseReviewsPages: s.publicCourseReviewsPages,
      fetchPublicCourseReviews: s.fetchPublicCourseReviews,
      fetchPublicLessonPreview: s.fetchPublicLessonPreview,

      publicJournals: s.publicJournals,
      publicJournalsLoading: s.publicJournalsLoading,
      publicJournalsError: s.publicJournalsError,
      publicJournalsTotal: s.publicJournalsTotal,
      publicJournalsPages: s.publicJournalsPages,
      fetchPublicJournals: s.fetchPublicJournals,

      publicJournal: s.publicJournal,
      publicJournalLoading: s.publicJournalLoading,
      publicJournalError: s.publicJournalError,
      fetchPublicJournal: s.fetchPublicJournal,

      publicResources: s.publicResources,
      publicResourcesLoading: s.publicResourcesLoading,
      publicResourcesError: s.publicResourcesError,
      publicResourcesTotal: s.publicResourcesTotal,
      publicResourcesPages: s.publicResourcesPages,
      fetchPublicResources: s.fetchPublicResources,
      fetchPublicResourceDetail: s.fetchPublicResourceDetail,
      fetchPublicResourcePreview: s.fetchPublicResourcePreview,

      publicLearningPaths: s.publicLearningPaths,
      publicLearningPathsLoading: s.publicLearningPathsLoading,
      publicLearningPathsError: s.publicLearningPathsError,
      publicLearningPathsTotal: s.publicLearningPathsTotal,
      publicLearningPathsPages: s.publicLearningPathsPages,
      fetchPublicLearningPaths: s.fetchPublicLearningPaths,

      publicLearningPath: s.publicLearningPath,
      publicLearningPathLoading: s.publicLearningPathLoading,
      publicLearningPathError: s.publicLearningPathError,
      fetchPublicLearningPath: s.fetchPublicLearningPath,

      publicSearchResults: s.publicSearchResults,
      publicSearchLoading: s.publicSearchLoading,
      publicSearchError: s.publicSearchError,
      publicSearchTotal: s.publicSearchTotal,
      publicSearchPages: s.publicSearchPages,
      fetchPublicSearch: s.fetchPublicSearch,
    }))
  );

/** Upload selectors (POST/DELETE /api/uploads). */
export const useUserUploads = () =>
  useUserStore(
    useShallow((s) => ({
      userImageUploadLoading: s.userImageUploadLoading,
      userImageUploadError: s.userImageUploadError,
      userLastImageUpload: s.userLastImageUpload,
      uploadUserImage: s.uploadUserImage,

      userDocumentUploadLoading: s.userDocumentUploadLoading,
      userDocumentUploadError: s.userDocumentUploadError,
      userLastDocumentUpload: s.userLastDocumentUpload,
      uploadUserDocument: s.uploadUserDocument,

      userUploadDeleteLoading: s.userUploadDeleteLoading,
      userUploadDeleteError: s.userUploadDeleteError,
      deleteUserUpload: s.deleteUserUpload,
    }))
  );

/** Generic messages + token helpers. */
export const useUserMessages = () =>
  useUserStore(
    useShallow((s) => ({
      successMessage: s.successMessage,
      errorMessage: s.errorMessage,
      clearMessage: s.clearMessage,
      getToken: s.getToken,
    }))
  );

/**
 * Surface the newest slice error on the shared `errorMessage` so a single
 * feedback toast (components/UserFeedback) can display any learner failure
 * without wiring every page to every `*Error` field.
 */
useUserStore.subscribe((state, prevState) => {
  for (const key of Object.keys(state)) {
    if (!key.endsWith("Error")) continue;
    const value = state[key];
    if (value && value !== prevState[key]) {
      useUserStore.setState({ errorMessage: value });
      break;
    }
  }
});

export default useUserStore;
